import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getBrands } from "../../services/admin/brand";
import { getCategory } from "../../services/admin/category";
import { getProduct } from "../../services/admin/product";

interface Product {
  id: number;
  product_name: string;
  product_slug: string;
  product_main_image: string;
  product_image: string[];
  sku: string;
  product_size: string;
  short_description: string;
  download_datasheet: string;
  download_more_info: string; 
  download_certificates: string;  
  product_video: string;
  category_id: number[];
  brand_id: number;
  status: string;
  created_at: string;
}

interface Brand {
  id: number;
  name: string;
}

interface Category {
  id: number;
  name: string;
}

const ProductSkeleton: React.FC = () => (
  <div className="w-full sm:w-1/2 md:w-1/3 2xl:w-1/4 max-w-full px-3 mb-6">
    <div className="animate-pulse flex flex-col justify-between items-center border border-[#C3C3C3] w-full h-[calc(100%-24px)] mb-6">
      <div className="bg-gray-300 h-[260px] w-full object-cover"></div>
      <div className="w-full p-4">
        <div className="h-6 bg-gray-300 mb-2 w-3/4 mx-auto rounded"></div>
        <div className="h-4 bg-gray-300 w-1/2 mx-auto rounded"></div>
      </div>
      <div className="h-10 bg-gray-300 w-40 mb-6 rounded"></div>
    </div>
  </div>
);

const ProductPage: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [openBrandId, setOpenBrandId] = useState<number | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [visibleCount, setVisibleCount] = useState(20);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const selectedBrandId = query.get("brand") ? parseInt(query.get("brand")!) : null;
  const selectedCategoryId = query.get("category") ? parseInt(query.get("category")!) : null;

  // Handle desktop/mobile resizing
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch brands, categories, products
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [brandRes, categoryRes, productRes] = await Promise.all([
        getBrands(),
        getCategory(),
        getProduct("publish"),
      ]);

      if (brandRes.success) setBrands(brandRes.data);
      if (categoryRes.success) setCategories(categoryRes.data);
      if (productRes.success) setProducts(productRes.data);

      setLoading(false);
    };

    fetchData();
  }, []);

  // Open accordion by brand ID from URL
  useEffect(() => {
    if (brands.length > 0 && selectedBrandId) {
      setOpenBrandId(selectedBrandId);
    }
  }, [brands, selectedBrandId]);

  // Toggle brand accordion
  const handleToggle = (brandId: number) => {
    const isSameBrand = openBrandId === brandId;
    setOpenBrandId(isSameBrand ? null : brandId);

    const params = new URLSearchParams(location.search);
    if (isSameBrand) {
      params.delete("brand");
      params.delete("category");
    } else {
      params.set("brand", brandId.toString());
      params.delete("category");
    }
    navigate({ pathname: "/products", search: params.toString() });
  };

  // Handle category checkbox
  const handleCategoryChange = (categoryId: number) => {
    const params = new URLSearchParams(location.search);
    if (selectedBrandId) params.set("brand", selectedBrandId.toString());

    if (selectedCategoryId === categoryId) {
      params.delete("category");
    } else {
      params.set("category", categoryId.toString());
    }

    navigate({ pathname: "/products", search: params.toString() });
  };

  // Filter products
  const filteredProducts = products.filter((p) => {
    const brandMatch = selectedBrandId ? p.brand_id === selectedBrandId : true;
    const categoryMatch = selectedCategoryId
      ? p.category_id.includes(selectedCategoryId)  // ✅ works with arrays
      : true;
    const searchMatch =
      searchTerm.trim() === ""
        ? true
        : p.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku.toLowerCase().includes(searchTerm.toLowerCase());
    return brandMatch && categoryMatch && searchMatch;
  });

  const visibleProducts = filteredProducts.slice(0, visibleCount);

  // Map brands to their categories
  const brandCategoryMap = brands.map((brand) => {
    const brandProducts = products.filter((p) => p.brand_id === brand.id);
    const catIds = Array.from(
      new Set(brandProducts.flatMap((p) => p.category_id))
    );
    const brandCategories = categories.filter((c) => catIds.includes(c.id));
    return { brand, categories: brandCategories };
  });

  const sortedBrandCategoryMap = brandCategoryMap.sort((a, b) => {
    if (a.brand.name === "Vaultex") return -1; // Vaultex first
    if (b.brand.name === "Vaultex") return 1;
    if (a.brand.name === "Honeywell") return 1; // Honeywell last
    if (b.brand.name === "Honeywell") return -1;
    return 0; // keep original order for others
  });

  return (
    <>
      {/* Breadcrumb */}
      <section className="pt-5 pb-8">
        <div className="w-full max-w-[1760px] mx-auto px-4">
          <div className="flex flex-wrap items-center -mx-4">
            <div className="w-full max-w-full px-4">
              <nav className="text-xs sm:text-lg font-light !leading-[1.5] text-[#020303]">
                <ol className="list-reset flex">
                  <li>
                    <Link
                      to="/"
                      className="font-medium text-[#009FE3] hover:underline transition-all duration-300 ease-in-out"
                    >
                      Home
                    </Link>
                  </li>
                  <li>
                    <span className="mx-2">/</span>
                  </li>
                  <li>
                    <span>Products</span>
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Product List */}
      <section className="pb-24 lg:pb-36">
        <div className="w-full max-w-[1760px] mx-auto px-4">
          <div className="flex flex-wrap -mx-3">
            {/* Sidebar */}
            <div className="w-full lg:w-1/4 xl:w-1/5 2xl:w-1/6 max-w-full px-3 mb-6 lg:mb-0">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setVisibleCount(20);
                }}
                placeholder="Search products..."
                className="text-base sm:text-[18px] font-light !leading-[1.5] text-[#020303] border py-3 px-5 placeholder-[#020303] outline-none w-full border-[#c3c3c3] hover:border-[#009fe3] focus:border-[#009fe3] mb-4 mt-[80px]"
              />
              <button
                onClick={() => setFilterOpen(!filterOpen)}
                className="flex justify-center lg:hidden w-full text-base xl:text-xl font-light leading-normal text-[#ffffff] border border-[#009FE3] bg-[#009FE3] py-[10px] px-[25px] xl:px-[30px] hover:bg-[#020303] hover:border-[#020303] transition-all duration-300 ease-in-out"
              >
                Product Filter
              </button>
              <AnimatePresence initial={false}>
                {(filterOpen || isDesktop) && (
                  <motion.div
                    key="accordion"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                    className="overflow-hidden bg-[#F2F4F6]"
                  >
                    <div className="bg-[#F2F4F6]">
                      {sortedBrandCategoryMap.map((entry) => (
                        <div key={entry.brand.id} className="border-b">
                          <button
                            onClick={() => handleToggle(entry.brand.id)}
                            className="w-full flex justify-between items-center text-base sm:text-lg lg:text-xl font-medium !leading-normal text-[#000000] py-4 px-5 text-left"
                          >
                            {entry.brand.name}{" "}
                            <svg
                              className="w-[20px]"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4 7L10 13.0197L16 7"
                                stroke="#009FE3"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          <AnimatePresence initial={false}>
                            {openBrandId === entry.brand.id && (
                              <motion.div
                                key="content"
                                initial="collapsed"
                                animate="open"
                                exit="collapsed"
                                variants={{
                                  open: { height: "auto", opacity: 1 },
                                  collapsed: { height: 0, opacity: 0 },
                                }}
                                transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                                className="overflow-hidden"
                              >
                                <div className="px-6 pb-5 flex flex-col gap-2 sm:gap-4">
                                  {entry.categories.map((cat) => (
                                    <label
                                      key={cat.id}
                                      className="cursor-pointer inline-flex items-center gap-2 text-sm font-light !leading-normal text-[#000000]"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedCategoryId === cat.id}
                                        onChange={() => handleCategoryChange(cat.id)}
                                        className="custom-checkbox w-4 h-4 accent-[#009FE3]"
                                      />
                                      {cat.name}
                                    </label>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Product Grid */}
            <div className="w-full lg:w-3/4 xl:w-4/5 2xl:w-5/6 max-w-full px-3">
              <div className="flex justify-between items-center pb-5">
                <h2 className="text-[26px] lg:text-[36px] xl:text-[40px] font-semibold !leading-[1.5] text-[#020303]">
                  {selectedCategoryId
                    ? categories.find((c) => c.id === selectedCategoryId)?.name || "Products"
                    : selectedBrandId
                      ? brands.find((b) => b.id === selectedBrandId)?.name || "Products"
                      : "Products"}
                </h2>
                <div className="text-[14px] sm:text-[16px] lg:text-[18px] xl:text-[20px] font-light !leading-[1.5] text-[#020303]">
                  {filteredProducts.length > 0 ? (
                    <>Showing {Math.min(visibleCount, filteredProducts.length)} out of {filteredProducts.length}</>
                  ) : (
                    <>No products found</>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap -mx-3">
                {loading
                  ? Array.from({ length: 20 }).map((_, idx) => <ProductSkeleton key={idx} />)
                  : visibleProducts.map((product) => (
                    <div key={product.id} className="w-full sm:w-1/2 md:w-1/3 2xl:w-1/4 max-w-full px-3">
                      <div className="flex flex-col justify-between items-center border border-[#C3C3C3] w-full h-[calc(100%-24px)] hover:border-[#009FE3] mb-6 group transition-all duration-300 ease-in-out">
                        {product.product_main_image && (
                          <img
                            src={product.product_main_image}
                            alt={product.product_name}
                            width="262"
                            height="233"
                            className="h-auto w-full max-h-[260px] object-cover"
                          />
                        )}
                        <div className="w-full p-4">
                          <Link
                            to={`/products/product-details/${product.id}`}
                            className="block w-full text-[18px] font-medium !leading-[1.5] text-[#020303] 
                                group-hover:text-[#009FE3] transition-all duration-300 ease-in-out 
                                text-center mb-2"
                          >
                            {product.product_name.split(" ").slice(0, 5).join(" ") +
                              (product.product_name.split(" ").length > 5 ? "..." : "")}
                          </Link>
                          <div className="text-[16px] font-light !leading-[1.5] text-[#020303] text-center">
                            SKU: <span className="font-bold">{product.sku}</span>
                          </div>
                        </div>
                        <Link
                          to={`/products/product-details/${product.id}`}
                          className="text-center text-base font-normal leading-normal text-[#FFFFFF] bg-[#009FE3] border-2 border-[#009FE3] py-[5px] px-[20px] hover:border-[#020303] hover:bg-[#020303] transition-all duration-300 ease-in-out mb-6 min-w-[169px]"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  ))}
                {visibleCount < filteredProducts.length && (
                  <div className="w-full max-w-full px-3 text-center mt-6">
                    <button
                      onClick={() => setVisibleCount((prev) => prev + 20)}
                      className="text-base xl:text-xl font-light leading-normal text-[#020303] border border-[#020303] py-[10px] px-[25px] xl:px-[30px] hover:text-[#FFFFFF] hover:bg-[#009FE3] hover:border-[#009FE3] transition-all duration-300 ease-in-out"
                    >
                      Load More
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductPage;
